import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { POLL_IDLE_MS } from "@/lib/realtime";
import { useLiveRefetchInterval } from "@/lib/socket";
import type {
  StrategyDetail,
  StrategyListItem,
  StrategySlideImages,
} from "@/types/strategy.type";

const listKey = (brandId: string | null) => ["socialStrategies", brandId];
const detailKey = (strategyId: string | null) => ["socialStrategy", strategyId];

// Exported so the agent rail and the realtime hints can refetch them.
export { listKey as strategyListKey, detailKey as strategyDetailKey };

export interface CreateStrategyVars {
  title?: string;
  periodScope: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string; // YYYY-MM-DD
}

/** What the page reads off the detail query. */
export interface StrategyDetailState {
  detail: StrategyDetail | null;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
}

/**
 * Lists a brand's strategies + create. `strategy.changed` hints refresh it
 * (strategyRealtime.ts); polling is only the safety net while the brand is live.
 */
export function useStrategies(brandId: string | null, opts?: { enabled?: boolean }) {
  const qc = useQueryClient();
  const refetchInterval = useLiveRefetchInterval(brandId, POLL_IDLE_MS);

  const query = useQuery({
    queryKey: listKey(brandId),
    queryFn: async () => {
      const res = await axiosInstance.get<{ strategies: StrategyListItem[] }>(
        `/social-strategies/${brandId}`,
      );
      return res.data.strategies;
    },
    enabled: !!brandId && (opts?.enabled ?? true),
    refetchInterval,
  });

  const createStrategy = useMutation({
    mutationFn: async (vars: CreateStrategyVars) => {
      const res = await axiosInstance.post<StrategyListItem>(
        `/social-strategies/${brandId}`,
        vars,
      );
      return res.data;
    },
    onSuccess: (row) =>
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) => (rows ? [row, ...rows] : [row])),
    onSettled: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });

  return {
    strategies: query.data ?? [],
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    createStrategy,
    invalidate: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  };
}

/** Rename one strategy. Allowed at any status. Optimistic. */
export function useRenameStrategy(brandId: string | null, strategyId: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (title: string) => {
      const res = await axiosInstance.patch<StrategyListItem>(
        `/social-strategies/${brandId}/${strategyId}`,
        { title },
      );
      return res.data;
    },
    onMutate: async (title) => {
      await qc.cancelQueries({ queryKey: listKey(brandId) });
      const previous = qc.getQueryData<StrategyListItem[]>(listKey(brandId))?.find((row) => row.id === strategyId)?.title;
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) =>
        rows?.map((row) => (row.id === strategyId ? { ...row, title } : row)),
      );
      return { previous };
    },
    onError: (_err, title, context) => {
      if (context?.previous == null) return;
      // Only undo our own edit: a newer rename may have landed meanwhile.
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) =>
        rows?.map((row) => (row.id === strategyId && row.title === title ? { ...row, title: context.previous! } : row)),
      );
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: listKey(brandId) });
      void qc.invalidateQueries({ queryKey: detailKey(strategyId) });
    },
  });
}

/** Delete one strategy. Optimistic: it leaves the list at once. */
export function useDeleteStrategy(brandId: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (strategyId: string) => {
      await axiosInstance.delete(`/social-strategies/${brandId}/${strategyId}`);
      return strategyId;
    },
    onMutate: async (strategyId) => {
      await qc.cancelQueries({ queryKey: listKey(brandId) });
      const removed = qc.getQueryData<StrategyListItem[]>(listKey(brandId))?.find((row) => row.id === strategyId);
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) => rows?.filter((row) => row.id !== strategyId));
      return { removed };
    },
    onError: (_err, _strategyId, context) => {
      if (!context?.removed) return;
      qc.setQueryData<StrategyListItem[]>(listKey(brandId), (rows) =>
        rows && !rows.some((row) => row.id === context.removed!.id) ? [context.removed!, ...rows] : rows,
      );
    },
    onSettled: () => qc.invalidateQueries({ queryKey: listKey(brandId) }),
  });
}

/** One strategy's full document, its pillar rows, its pictures and its checks. */
export function useStrategyDetail(brandId: string | null, strategyId: string | null): StrategyDetailState {
  const qc = useQueryClient();
  const refetchInterval = useLiveRefetchInterval(brandId, POLL_IDLE_MS);

  const query = useQuery({
    queryKey: detailKey(strategyId),
    queryFn: async () => {
      const res = await axiosInstance.get<StrategyDetail>(
        `/social-strategies/${brandId}/${strategyId}`,
      );
      return res.data;
    },
    enabled: !!brandId && !!strategyId,
    refetchInterval,
  });

  return {
    detail: query.data ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: () => void qc.invalidateQueries({ queryKey: detailKey(strategyId) }),
  };
}

/**
 * Assign or clear one picture slot. Allowed at any status. A slot holds ONE
 * thing: pass exactly one of mediaId / referenceId / trendExampleId, or
 * `mediaId: null` to clear it.
 */
export interface StrategySlideMediaVars {
  slotKey: string;
  mediaId?: string | null;
  referenceId?: string;
  trendExampleId?: string;
}

export function useStrategySlideImage(brandId: string | null, strategyId: string | null) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (vars: StrategySlideMediaVars) => {
      const res = await axiosInstance.put<{ slideImages: StrategySlideImages }>(
        `/social-strategies/${brandId}/${strategyId}/slide-images`,
        vars,
      );
      return res.data;
    },
    onMutate: async (vars) => {
      // A cleared slot goes at once; a new picture waits for its presigned URL.
      if (vars.mediaId !== null || vars.referenceId || vars.trendExampleId) return {};
      await qc.cancelQueries({ queryKey: detailKey(strategyId) });
      const previous = qc.getQueryData<StrategyDetail>(detailKey(strategyId))?.slideImages[vars.slotKey];
      qc.setQueryData<StrategyDetail>(detailKey(strategyId), (detail) => {
        if (!detail) return detail;
        const rest = { ...detail.slideImages };
        delete rest[vars.slotKey];
        return { ...detail, slideImages: rest };
      });
      return { previous };
    },
    onError: (_err, vars, context) => {
      if (!context?.previous) return;
      qc.setQueryData<StrategyDetail>(detailKey(strategyId), (detail) =>
        detail ? { ...detail, slideImages: { ...detail.slideImages, [vars.slotKey]: context.previous! } } : detail,
      );
    },
    onSuccess: (data) =>
      qc.setQueryData<StrategyDetail>(detailKey(strategyId), (detail) =>
        detail ? { ...detail, slideImages: data.slideImages } : detail,
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: detailKey(strategyId) }),
  });
}
